<script setup>
import { ref, computed } from 'vue';
import TextArea from './TextArea.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
  label: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  maxLength: { type: Number, default: 200 },
  id: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  rows: { type: [String, Number], default: 4 },
  message: { type: String, default: '' },
  messageType: {
    type: String,
    default: 'info',
    validator: value => ['info', 'error', 'success'].includes(value),
  },
});

const emit = defineEmits(['update:modelValue']);

const isExpanded = ref(false);
const expandedValue = ref('');

const localValue = computed({
  get: () => props.modelValue,
  set: val => emit('update:modelValue', val),
});

const openExpanded = () => {
  expandedValue.value = props.modelValue;
  isExpanded.value = true;
};

const saveAndClose = () => {
  emit('update:modelValue', expandedValue.value);
  isExpanded.value = false;
};

const cancelExpanded = () => {
  isExpanded.value = false;
};
</script>

<template>
  <div class="relative">
    <!-- Normal TextArea when not expanded -->
    <div v-if="!isExpanded">
      <TextArea
        :id="id"
        v-model="localValue"
        :label="label"
        :placeholder="placeholder"
        :max-length="maxLength"
        :disabled="disabled"
        :message="message"
        :message-type="messageType"
        :rows="rows"
        class="w-full"
      />
      <button
        type="button"
        class="absolute top-8 right-2 p-1 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-black2 rounded transition-colors"
        :title="$t('AGENT_BOTS.FORM.EXPAND_EDITOR')"
        @click="openExpanded"
      >
        <span class="i-lucide-expand text-sm" />
      </button>
    </div>

    <!-- Expanded editor - replaces the normal textarea in place -->
    <div v-else class="expanded-editor">
      <div class="flex items-center justify-between mb-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ label }}
        </label>
        <button
          type="button"
          class="p-1 text-n-slate-10 hover:text-n-slate-12 rounded transition-colors"
          :title="$t('AGENT_BOTS.FORM.EXPAND_EDITOR')"
          @click="cancelExpanded"
        >
          <span class="i-lucide-minimize-2 text-sm" />
        </button>
      </div>

      <textarea
        v-model="expandedValue"
        :placeholder="placeholder"
        class="w-full h-[300px] p-3 text-sm border rounded-lg resize-none bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:border-n-brand focus:outline-none"
      />

      <div class="flex justify-end gap-2 mt-2">
        <NextButton
          faded
          slate
          size="small"
          :label="$t('AGENT_BOTS.FORM.CANCEL')"
          @click="cancelExpanded"
        />
        <NextButton
          size="small"
          :label="$t('AGENT_BOTS.FORM.SAVE')"
          @click="saveAndClose"
        />
      </div>
    </div>
  </div>
</template>
